import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, useTheme, Chip, Grid, Divider, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { ContractExpiryForecast as ContractExpiryForecastType } from '../../../types/utility.types';

interface ContractExpiryForecastProps {
  data: ContractExpiryForecastType[];
  isLoading: boolean;
}

const ContractExpiryForecast: React.FC<ContractExpiryForecastProps> = ({ data, isLoading }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);

  const handleClientClick = (clientId: string) => {
    navigate(`/utilities/clients/${clientId}`);
  };

  const handleBarClick = (data: any) => {
    if (data && data.activeLabel) {
      setSelectedPeriod(data.activeLabel);
    }
  };

  const getFilteredContracts = () => {
    if (!selectedPeriod) return [];
    
    const periodData = chartData.find(item => item.formattedPeriod === selectedPeriod);
    return periodData ? periodData.services : [];
  };

  const getUrgencyColor = (daysUntilExpiry: number, isOverdue: boolean) => {
    if (isOverdue) return 'error';
    if (daysUntilExpiry <= 30) return 'error';
    if (daysUntilExpiry <= 60) return 'warning';
    if (daysUntilExpiry <= 90) return 'info';
    return 'success';
  };

  const getUrgencyLabel = (daysUntilExpiry: number, isOverdue: boolean) => {
    if (isOverdue) return 'OVERDUE';
    if (daysUntilExpiry <= 30) return 'CRITICAL';
    if (daysUntilExpiry <= 60) return 'WARNING';
    return 'OK';
  };

  const customTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          color: 'rgba(255, 255, 255, 0.9)',
          padding: '8px 12px',
          fontSize: '14px'
        }}>
          <p style={{ margin: 0 }}>{payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Contract Expiry Forecast
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Format the period for display (YYYY-MM to Month YYYY)
  const formatPeriod = (period: string) => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('default', { month: 'short', year: 'numeric' });
  };

  // Sort data by period
  const chartData = [...data].sort((a, b) => {
    const periodA = a.period.split('-').map(Number);
    const periodB = b.period.split('-').map(Number);
    
    if (periodA[0] !== periodB[0]) {
      return periodA[0] - periodB[0];
    }
    return periodA[1] - periodB[1];
  }).map(item => ({
    ...item,
    formattedPeriod: formatPeriod(item.period)
  }));

  // Get the first 3 months for detailed view
  const upcomingMonths = chartData.slice(0, 3);

  // Get service type color
  const getServiceTypeColor = (serviceType: string) => {
    const colors: Record<string, string> = {
      'Gas': '#FF8042',
      'Electricity': '#FFBB28',
      'Water': '#0088FE',
      'Telecoms': '#00C49F',
      'Waste': '#8884d8',
      'Merchant Services': '#82ca9d',
      'PPA Export Contract': '#ffc658',
      'EV': '#a4de6c',
      'Renewable': '#d0ed57',
      'Insurance': '#83a6ed'
    };
    return colors[serviceType] || theme.palette.primary.main;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Contract Expiry Forecast
        </Typography>
        {chartData.length > 0 ? (
          <>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                  onClick={handleBarClick}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="formattedPeriod" 
                    tick={{ fontSize: 14, fill: theme.palette.text.primary }}
                  />
                  <YAxis tick={{ fontSize: 14, fill: theme.palette.text.primary }} />
                  <Tooltip content={customTooltip} />
                  <Bar dataKey="count" name="Contracts Expiring" fill={theme.palette.primary.main} style={{ cursor: 'pointer' }} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            
            {selectedPeriod ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#fff' }}>
                  Contracts Expiring in {selectedPeriod}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Client</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Service Type</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Supplier</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Contract End Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredContracts().map((service, index) => {
                        const endDate = new Date(service.contractEndDate);
                        const today = new Date();
                        const daysUntilExpiry = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                        const isOverdue = daysUntilExpiry < 0;
                        
                        return (
                          <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(255,255,255,0.02)' } }}>
                            <TableCell>
                              <Chip
                                label={getUrgencyLabel(daysUntilExpiry, isOverdue)}
                                color={getUrgencyColor(daysUntilExpiry, isOverdue)}
                                size="small"
                                sx={{ fontWeight: 'bold', minWidth: '80px' }}
                              />
                            </TableCell>
                            <TableCell>
                              {service.client?.companyName ? (
                                <Link
                                  component="button"
                                  variant="inherit"
                                  onClick={() => handleClientClick(service.client!._id)}
                                  sx={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    '&:hover': {
                                      textDecoration: 'none',
                                      color: '#e0e0e0'
                                    }
                                  }}
                                >
                                  {service.client.companyName}
                                </Link>
                              ) : (
                                <Typography sx={{ color: '#fff' }}>Unknown Client</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ color: '#fff' }}>{service.serviceType}</TableCell>
                            <TableCell sx={{ color: '#fff' }}>
                              {typeof service.supplier === 'object' && service.supplier !== null && 'name' in service.supplier
                                ? (service.supplier as any).name
                                : typeof service.supplier === 'string'
                                ? service.supplier
                                : 'N/A'}
                            </TableCell>
                            <TableCell sx={{ color: '#fff' }}>
                              {endDate.toLocaleDateString()}
                              {isOverdue && (
                                <Typography variant="caption" sx={{ color: 'error.main', display: 'block' }}>
                                  ({Math.abs(daysUntilExpiry)} days overdue)
                                </Typography>
                              )}
                              {!isOverdue && daysUntilExpiry <= 90 && (
                                <Typography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
                                  ({daysUntilExpiry} days remaining)
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setSelectedPeriod(null)}
                    sx={{ 
                      color: theme.palette.primary.light,
                      textDecoration: 'underline',
                      '&:hover': {
                        textDecoration: 'none'
                      }
                    }}
                  >
                    ← Back to overview
                  </Link>
                </Box>
              </Box>
            ) : upcomingMonths.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#fff' }}>
                  Contracts Expiring Soon (Click chart bars for details)
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {upcomingMonths.map((month, index) => (
                  <Box key={month.period} sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#fff' }}>
                      {month.formattedPeriod} - {month.count} contract{month.count !== 1 ? 's' : ''}
                    </Typography>
                    
                    <Grid container spacing={1}>
                      {month.services.map((service, serviceIndex) => (
                        <Grid item key={`contract-${serviceIndex}`} xs={12}>
                          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                            <Typography 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: '#fff', 
                                fontSize: '1.1rem',
                                mb: 1,
                                display: 'inline'
                              }}
                            >
                              {service.client?.companyName ? (
                                <Link
                                  component="button"
                                  variant="inherit"
                                  onClick={() => handleClientClick(service.client!._id)}
                                  sx={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    fontSize: '1.1rem',
                                    '&:hover': {
                                      textDecoration: 'none',
                                      color: '#e0e0e0'
                                    }
                                  }}
                                >
                                  {service.client.companyName}
                                </Link>
                              ) : (
                                <span style={{ fontWeight: 'bold', color: '#fff' }}>Unknown Client</span>
                              )} - {service.serviceType}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Chip 
                                label={typeof service.supplier === 'object' && service.supplier !== null && 'name' in service.supplier
                                  ? (service.supplier as any).name
                                  : typeof service.supplier === 'string'
                                  ? service.supplier
                                  : 'N/A'} 
                                size="medium"
                                sx={{ 
                                  backgroundColor: getServiceTypeColor(service.serviceType),
                                  color: '#fff',
                                  fontWeight: 'bold',
                                  fontSize: '0.875rem',
                                  '& .MuiChip-label': {
                                    px: 2,
                                    py: 0.5
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    
                    {index < upcomingMonths.length - 1 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>No data available</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ContractExpiryForecast;
